import {
  ENTITY_TABLES,
  MAX_PUSH_MUTATIONS,
  DEFAULT_PULL_LIMIT,
  type PullResponse,
  type PushRequest,
  type PushResponse,
} from '@dairy/shared';
import type { DairyDB, OutboxEntry } from '@/db/db';

/** The network side of sync; replaced by a fake in unit tests. Network failures throw. */
export interface SyncTransport {
  push(body: PushRequest): Promise<{ status: number; body?: PushResponse }>;
  pull(cursor: number, limit: number): Promise<{ status: number; body?: PullResponse }>;
  uploadFile(id: string, blob: Blob, mimeType: string): Promise<number>;
  /** Single-flight token refresh; true when a new access token is available. */
  refresh(): Promise<boolean>;
  deviceId(): Promise<string>;
}

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error' | 'auth';

export interface SyncStatus {
  state: SyncState;
  lastSyncAt: string | null;
  pending: number;
  rejected: number;
}

export interface SyncHooks {
  /** Reject a pull if the authenticated session changes while it is in flight. */
  capturePullSession?: () => () => boolean;
  /** Tenant settings or health protocols changed in a pull → full reminder reconcile (S3-T10). */
  onFarmRulesChanged?: () => Promise<void>;
  onTenant?: (tenant: PullResponse['tenant'], users: PullResponse['users']) => Promise<void>;
  /** After every successful sync (daily reconcile). */
  afterSync?: () => Promise<void>;
}

type Outcome = 'ok' | 'network' | 'auth' | 'server';

/** §11.4: retry after 5 s, 15 s, 60 s, then every 60 s. */
export const RETRY_DELAYS_MS = [5_000, 15_000, 60_000];
export const SYNC_INTERVAL_MS = 60_000;
export const SAVE_DEBOUNCE_MS = 2_000;

export class SyncEngine {
  private running: Promise<Outcome> | null = null;
  private retryIndex = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private status: SyncStatus = { state: 'idle', lastSyncAt: null, pending: 0, rejected: 0 };
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly db: DairyDB,
    private readonly transport: SyncTransport,
    private readonly hooks: SyncHooks = {},
  ) {}

  getStatus(): SyncStatus {
    return this.status;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private setStatus(patch: Partial<SyncStatus>): void {
    this.status = { ...this.status, ...patch };
    for (const l of this.listeners) l();
  }

  async refreshCounts(): Promise<void> {
    const [pending, rejected] = await Promise.all([
      this.db.outbox.where('status').equals('pending').count(),
      this.db.outbox.where('status').equals('rejected').count(),
    ]);
    const lastSyncAt = (await this.db.meta.get('lastSyncAt'))?.value as string | undefined;
    this.setStatus({ pending, rejected, lastSyncAt: lastSyncAt ?? this.status.lastSyncAt });
  }

  /** One sync run; if a run is already going, its result is returned (§11.4 step 1). */
  run(): Promise<Outcome> {
    this.running ??= this.doRun().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  isRunning(): boolean {
    return this.running !== null;
  }

  /** Cancel a scheduled retry (logout, tests). */
  stop(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.retryIndex = 0;
  }

  private async doRun(): Promise<Outcome> {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.setStatus({ state: 'syncing' });
    let outcome: Outcome;
    try {
      outcome = await this.push();
      if (outcome === 'ok') outcome = await this.pull();
      if (outcome === 'ok') await this.uploadFiles();
    } catch {
      outcome = 'network';
    }
    if (outcome === 'ok') {
      this.retryIndex = 0;
      const now = new Date().toISOString();
      await this.db.meta.put({ key: 'lastSyncAt', value: now });
      this.setStatus({ state: 'idle', lastSyncAt: now });
      try {
        await this.hooks.afterSync?.();
      } catch {
        /* reminders are recomputed next time */
      }
    } else {
      this.setStatus({ state: outcome === 'network' ? 'offline' : outcome === 'auth' ? 'auth' : 'error' });
      if (outcome !== 'auth') this.scheduleRetry();
    }
    await this.refreshCounts();
    return outcome;
  }

  private scheduleRetry(): void {
    const delay = RETRY_DELAYS_MS[Math.min(this.retryIndex, RETRY_DELAYS_MS.length - 1)]!;
    this.retryIndex += 1;
    this.retryTimer = setTimeout(() => void this.run(), delay);
  }

  /** §11.4 step 2. */
  async push(): Promise<Outcome> {
    let refreshed = false;
    for (;;) {
      const batch: OutboxEntry[] = await this.db.outbox
        .where('status')
        .equals('pending')
        .sortBy('seq')
        .then((rows) => rows.slice(0, MAX_PUSH_MUTATIONS));
      if (batch.length === 0) return 'ok';
      const body: PushRequest = {
        deviceId: await this.transport.deviceId(),
        mutations: batch.map((o) => ({
          mutationId: o.mutationId,
          entity: o.entity,
          op: o.op,
          id: o.recordId,
          ...(o.data ? { data: o.data } : {}),
          clientTs: o.clientTs,
        })),
      };
      let res: { status: number; body?: PushResponse };
      try {
        res = await this.transport.push(body);
      } catch {
        return 'network';
      }
      if (res.status === 401) {
        if (!refreshed && (await this.transport.refresh())) {
          refreshed = true;
          continue;
        }
        return 'auth';
      }
      if (res.status >= 500 || !res.body) return res.status === 0 ? 'network' : 'server';
      if (res.status >= 400) return 'server';
      const results = res.body.results;
      await this.db.transaction('rw', this.db.outbox, async () => {
        for (const [i, r] of results.entries()) {
          const entry = batch[i];
          if (!entry || entry.mutationId !== r.mutationId) continue;
          if (r.status === 'applied' || r.status === 'duplicate') await this.db.outbox.delete(entry.seq!);
          else await this.db.outbox.update(entry.seq!, { status: 'rejected', error: r.error });
        }
      });
    }
  }

  /** §11.4 step 3. */
  async pull(): Promise<Outcome> {
    let refreshed = false;
    let rulesChanged = false;
    for (;;) {
      const cursor = ((await this.db.meta.get('cursor'))?.value as number | undefined) ?? 0;
      const isCurrentSession = this.hooks.capturePullSession?.();
      let res: { status: number; body?: PullResponse };
      try {
        res = await this.transport.pull(cursor, DEFAULT_PULL_LIMIT);
      } catch {
        return 'network';
      }
      if (res.status === 401) {
        if (!refreshed && (await this.transport.refresh())) {
          refreshed = true;
          continue;
        }
        return 'auth';
      }
      if (res.status !== 200 || !res.body) return 'server';
      const page = res.body;
      const previousTenant = (await this.db.meta.get('tenant'))?.value as PullResponse['tenant'] | undefined;
      if (previousTenant && previousTenant.updatedAt !== page.tenant.updatedAt) rulesChanged = true;
      const tableNames = [...new Set(page.changes.map((c) => ENTITY_TABLES[c.entity].local))];
      await this.db.transaction('rw', [...tableNames.map((n) => this.db.table(n)), this.db.outbox, this.db.meta], async () => {
        if (isCurrentSession && !isCurrentSession()) throw new Error('Session changed during pull');
        for (const c of page.changes) {
          // The local change wins until it is pushed or resolved.
          const local = await this.db.outbox.where('recordId').equals(c.id).count();
          if (local > 0) continue;
          const table = this.db.table(ENTITY_TABLES[c.entity].local);
          if (c.deleted) await table.delete(c.id);
          else await table.put(c.data!);
          if (c.entity === 'healthProtocol') rulesChanged = true;
        }
        await this.db.meta.put({ key: 'cursor', value: page.cursor });
        await this.db.meta.put({ key: 'tenant', value: page.tenant });
        await this.db.meta.put({ key: 'users', value: page.users });
      });
      await this.hooks.onTenant?.(page.tenant, page.users);
      if (!page.hasMore) break;
    }
    if (rulesChanged) await this.hooks.onFarmRulesChanged?.();
    return 'ok';
  }

  /** S4-T11: upload photo blobs whose FileAsset record reached the server. */
  async uploadFiles(): Promise<void> {
    const blobs = await this.db.fileBlobs.where('status').equals('pending').toArray();
    for (const b of blobs) {
      if ((await this.db.outbox.where('recordId').equals(b.id).count()) > 0) continue;
      const asset = await this.db.fileAssets.get(b.id);
      if (!asset) continue;
      try {
        const status = await this.transport.uploadFile(b.id, b.blob, asset.mimeType);
        if (status === 200) await this.db.fileBlobs.update(b.id, { status: 'uploaded' });
      } catch {
        return; // offline: try next time
      }
    }
  }
}
